/**
 * Tabs primitive -- Leviathan design system.
 *
 * Active-state: animated underline using framer-motion layoutId.
 * The underline slides at motion.duration.fast (160ms) to follow the active tab.
 *
 * `reducedMotion()` disables the slide animation for users who prefer it.
 *
 * API is backward compatible with the previous shadcn Tabs export.
 *
 * Do not add em-dashes anywhere.
 */

'use client'

import { createContext, useContext, useState } from 'react'
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'
import { motion, useReducedMotion } from 'framer-motion'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { motion as motionTokens } from '@/design-system/tokens'

/**
 * Shares the per-TabsList layoutId down to every TabsTrigger so that
 * multiple independent Tabs on the same page do not share the same
 * framer-motion LayoutGroup underline element.
 */
const TabsLayoutIdContext = createContext<string>('tabs-underline')

function Tabs({
  className,
  orientation = 'horizontal',
  ...props
}: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      className={cn('group/tabs flex gap-2 data-horizontal:flex-col', className)}
      {...props}
    />
  )
}

const tabsListVariants = cva(
  'group/tabs-list relative inline-flex w-fit items-center justify-center p-[3px] text-muted-foreground group-data-horizontal/tabs:h-8 group-data-vertical/tabs:h-fit group-data-vertical/tabs:flex-col',
  {
    variants: {
      variant: {
        default: 'rounded-lg bg-muted',
        line: 'gap-1 rounded-none border-b border-white/8 bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

let _tabsInstanceCounter = 0

function TabsList({
  className,
  variant = 'default',
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof tabsListVariants>) {
  const [layoutId] = useState(() => {
    _tabsInstanceCounter += 1
    return `tabs-underline-${_tabsInstanceCounter}`
  })

  return (
    <TabsLayoutIdContext.Provider value={layoutId}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        data-variant={variant}
        className={cn(tabsListVariants({ variant }), className)}
        {...props}
      />
    </TabsLayoutIdContext.Provider>
  )
}

function TabsTrigger({
  className,
  children,
  ...props
}: TabsPrimitive.Tab.Props) {
  const shouldReduceMotion = useReducedMotion()
  const layoutId = useContext(TabsLayoutIdContext)

  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        'relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-sm font-medium whitespace-nowrap text-foreground/60 transition-colors group-data-vertical/tabs:w-full group-data-vertical/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
        // default variant active state
        'group-data-[variant=default]/tabs-list:data-active:bg-background group-data-[variant=default]/tabs-list:data-active:text-foreground group-data-[variant=default]/tabs-list:data-active:shadow-sm',
        // line variant active text
        'group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-active:bg-transparent group-data-[variant=line]/tabs-list:data-active:text-foreground',
        className,
      )}
      render={({ ref, ...renderProps }) => {
        const dataProps = renderProps as Record<string, unknown>
        return (
        <button ref={ref} {...renderProps}>
          {children}
          {/* Animated underline for line variant */}
          {dataProps['data-active'] !== undefined && dataProps['data-active'] !== false && (
            <motion.span
              layoutId={layoutId}
              className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-gold"
              style={{ originX: 0.5 }}
              transition={
                shouldReduceMotion
                  ? { duration: 0 }
                  : {
                      duration: motionTokens.duration.fast / 1000,
                      ease: motionTokens.ease.standard as [number, number, number, number],
                    }
              }
            />
          )}
        </button>
        )
      }}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn('flex-1 text-sm outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
